import axiosInstance from '../config/axios.config';

/**
 * Loan Application API Service
 * 
 * Provides methods to interact with loan application APIs
 */
class LoanApplicationService {
    
    /**
     * Get all loan applications with optional filters
     */
    async getLoanApplications(filters = {}) {
        try {
            const response = await axiosInstance.get('/loan-applications', { params: filters });
            return response.data;
        } catch (error) {
            console.error('Error fetching loan applications:', error);
            throw error;
        }
    }

    /**
     * Get specific loan application by ID
     */
    async getLoanApplication(id) {
        try {
            const response = await axiosInstance.get(`/loan-applications/${id}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching loan application:', error);
            throw error;
        }
    }

    /**
     * Create new loan application
     */
    async createLoanApplication(applicationData) {
        try {
            const response = await axiosInstance.post('/loan-applications', applicationData);
            return response.data;
        } catch (error) {
            console.error('Error creating loan application:', error);
            throw error;
        }
    }

    /**
     * Update loan application
     */
    async updateLoanApplication(id, applicationData) {
        try {
            const response = await axiosInstance.put(`/loan-applications/${id}`, applicationData);
            return response.data;
        } catch (error) {
            console.error('Error updating loan application:', error);
            throw error;
        }
    }

    /**
     * Submit application for approval
     */
    async submitApplication(id) {
        try {
            const response = await axiosInstance.post(`/loan-applications/${id}/submit`);
            return response.data;
        } catch (error) {
            console.error('Error submitting application:', error);
            throw error;
        }
    }

    /**
     * Change application status
     */
    async changeStatus(id, status, reason = null) {
        try {
            const response = await axiosInstance.post(`/loan-applications/${id}/change-status`, {
                status: status,
                reason: reason
            });
            return response.data;
        } catch (error) {
            console.error('Error changing status:', error);
            throw error;
        }
    }

    /**
     * Assign application to user
     */
    async assignApplication(id, userId, reason = null) {
        try {
            const response = await axiosInstance.post(`/loan-applications/${id}/assign`, {
                user_id: userId,
                reason: reason
            });
            return response.data;
        } catch (error) {
            console.error('Error assigning application:', error);
            throw error;
        }
    }

    /**
     * Get application statistics
     */
    async getStatistics() {
        try {
            const response = await axiosInstance.get('/loan-applications/statistics');
            return response.data;
        } catch (error) {
            console.error('Error fetching statistics:', error);
            throw error;
        }
    }

    /**
     * Get workflow history
     */
    async getWorkflowHistory(id) {
        try {
            const response = await axiosInstance.get(`/loan-applications/${id}/workflow-history`);
            return response.data;
        } catch (error) {
            console.error('Error fetching workflow history:', error);
            throw error;
        }
    }

    /**
     * Perform credit assessment
     */
    async performCreditAssessment(id) {
        try {
            const response = await axiosInstance.post(`/loan-applications/${id}/credit-assessment`);
            return response.data;
        } catch (error) {
            console.error('Error performing credit assessment:', error);
            throw error;
        }
    }

    /**
     * Generate loan agreement
     */
    async generateAgreement(id) {
        try {
            const response = await axiosInstance.post(`/loan-applications/${id}/generate-agreement`);
            return response.data;
        } catch (error) {
            console.error('Error generating agreement:', error);
            throw error;
        }
    }

    /**
     * Upload document for application
     */
    async uploadDocument(applicationId, documentData) {
        try {
            const formData = new FormData();
            Object.keys(documentData).forEach(key => {
                if (documentData[key] instanceof File) {
                    formData.append(key, documentData[key]);
                } else {
                    formData.append(key, documentData[key]);
                }
            });

            const response = await axiosInstance.post(
                `/loan-applications/${applicationId}/documents`, 
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );
            return response.data;
        } catch (error) {
            console.error('Error uploading document:', error);
            throw error;
        }
    }

    /**
     * Verify document
     */
    async verifyDocument(applicationId, documentId, verificationData) {
        try {
            const response = await axiosInstance.post(
                `/loan-applications/${applicationId}/documents/${documentId}/verify`, 
                verificationData
            );
            return response.data;
        } catch (error) {
            console.error('Error verifying document:', error);
            throw error;
        }
    }

    /**
     * Reject document
     */
    async rejectDocument(applicationId, documentId, rejectionData) {
        try {
            const response = await axiosInstance.post(
                `/loan-applications/${applicationId}/documents/${documentId}/reject`, 
                rejectionData
            );
            return response.data;
        } catch (error) {
            console.error('Error rejecting document:', error);
            throw error;
        }
    }
}

export default new LoanApplicationService();